import type { World } from './World'

export const Stage = {
  /** Poll devices, build the normalised input frame. Every rendered frame. */
  Input: 'Input',
  /** Deterministic gameplay. Fixed 60 Hz, may run several times per frame. */
  Simulation: 'Simulation',
  /** Reactions to simulation events that still belong to the fixed step. */
  PostSim: 'PostSim',
  /** Interpolated output: camera, meshes, VFX, audio, HUD. Every frame. */
  Presentation: 'Presentation',
} as const

export type StageName = (typeof Stage)[keyof typeof Stage]

const STAGE_ORDER: StageName[] = [
  Stage.Input,
  Stage.Simulation,
  Stage.PostSim,
  Stage.Presentation,
]

export interface SystemContext {
  readonly world: World
  /** Fixed step in Simulation/PostSim; real frame delta in Input/Presentation. */
  readonly dt: number
  /** Interpolation factor 0..1 between the previous and current fixed step. */
  readonly alpha: number
  /** Seconds since the game started (real time). */
  readonly elapsed: number
}

export type SystemFn = (ctx: SystemContext) => void

interface RegisteredSystem {
  readonly name: string
  readonly fn: SystemFn
  readonly order: number
  enabled: boolean
  lastMs: number
}

/**
 * Runs systems grouped into stages, in a fixed, explicit order. There is no
 * automatic dependency resolution on purpose: for a game this size, a readable
 * hand-written order is easier to reason about than an inferred one.
 */
export class Scheduler {
  private readonly stages = new Map<StageName, RegisteredSystem[]>()
  private profiling = false

  constructor() {
    for (const stage of STAGE_ORDER) this.stages.set(stage, [])
  }

  add(stage: StageName, name: string, fn: SystemFn, order = 0): this {
    const list = this.stages.get(stage)
    if (!list) throw new Error(`Unknown stage: ${stage}`)
    list.push({ name, fn, order, enabled: true, lastMs: 0 })
    list.sort((a, b) => a.order - b.order)
    return this
  }

  setEnabled(name: string, enabled: boolean): void {
    for (const list of this.stages.values()) {
      for (const sys of list) if (sys.name === name) sys.enabled = enabled
    }
  }

  setProfiling(on: boolean): void {
    this.profiling = on
  }

  run(stage: StageName, ctx: SystemContext): void {
    const list = this.stages.get(stage)
    if (!list) return
    for (const sys of list) {
      if (!sys.enabled) continue
      if (this.profiling) {
        const t0 = performance.now()
        sys.fn(ctx)
        sys.lastMs = performance.now() - t0
      } else {
        sys.fn(ctx)
      }
    }
    // Deferred destruction lands here: every system in the stage saw a
    // consistent world, and queries rebuild before the next stage runs.
    ctx.world.flushDestroyed()
  }

  timings(): Array<{ stage: StageName; name: string; ms: number }> {
    const out: Array<{ stage: StageName; name: string; ms: number }> = []
    for (const stage of STAGE_ORDER) {
      for (const sys of this.stages.get(stage)!) {
        out.push({ stage, name: sys.name, ms: sys.lastMs })
      }
    }
    return out
  }
}
